<template>
  <div class="ak-input" :class="wrapClasses">
    <ak-icon
      v-if="hasLeftIcon"
      class="ak-input__icon ak-input__icon--before color-muted"
      :class="[iconErrorClass, { 'ak-input__icon-clickable': iconClickable }]"
      :type="icon"
      @click="iconClick"
    />

    <input
      v-if="type !== 'textarea'"
      ref="input"
      class="ak-input__control ak-form-control"
      v-bind="$attrs"
      :value="computedValue"
      :type="newType"
      :class="[inputErrorClass, disabledClass]"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    />

    <textarea
      v-else
      ref="input"
      v-bind="$attrs"
      :value="computedValue"
      class="ak-input__control ak-form-control"
      :class="[inputErrorClass, expandableClass, disabledClass]"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    >
    </textarea>

    <div v-if="isLoading" class="ak-input__icon ak-input__icon--after">
      <ak-loading-spinner
        size="tiny"
        class="color-primary"
        @click="rightIconClick"
      />
    </div>

    <ak-icon
      v-else-if="hasIconRight"
      :type="rightIcon"
      class="ak-input__icon ak-input__icon--after"
      :class="[iconErrorClass]"
      @click="rightIconClick"
    />

    <ak-icon
      v-if="inlineEdit && !isFocused"
      type="edit"
      class="ak-input__icon ak-input__icon--after"
      size="small"
    />

    <ak-button
      v-if="inlineEdit && isFocused"
      class="ml-small"
      size="tiny"
    >
      Ok
    </ak-button>

    <div
      v-if="maxlength && hasCounter && type !== 'number'"
      class="ak-input__counter"
      :class="{ invisible: !isFocused }"
    >
      {{ valueLength }} / {{ maxlength }}
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'

import {
  inputProps,
  formElementProps,
  validationProps,
} from '../../mixins/formPropsMixin'

import validationMixin from '../../mixins/validationMixin'
import formElementMixin from '../../mixins/formElementMixin'
import Icon from '../icon/Icon.vue'
import LoadingSpinner from '../loading/LoadingSpinner.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-input />
   */
  name: 'AkInput',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [LoadingSpinner.name]: LoadingSpinner,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...inputProps,
    ...formElementProps,
    ...validationProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue', 'rightIconClick', 'iconClick', 'blur', 'focus'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const input = ref(null)
    const newType = ref(props.type)
    const isPasswordVisible = ref(false)

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: props.type,
      emit,
      props,
      attrs,
      input,
    })

    /**
     * @type {Object}
     * @description The form element mixin return value, this is an object
     * that contains all of the shared functionality for form elements
     */
    const formMixin = formElementMixin({
      emit,
      props,
      attrs,
      input,
      validation,
    })

    /**
     * @type {Mixed}
     * @description This is the model that we will apply to the input this
     * will allow us to modify the local value and the emit will update
     * the modelValue passed in via the parent component.
     */
    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @returns {Number|Undefined}
     * @description Determines if the left icon should show
     */
    const hasLeftIcon = computed(() => {
      return props.icon && props.icon.length
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine whether or not to
     * display the right icon
     */
    const hasIconRight = computed(() => {
      return (
        (props.type === 'search' && valueLength.value) ||
        props.passwordReveal ||
        props.statusTypeIcon
      )
    })

    /**
     * @returns {String}
     * @description Returns the right icon type
     */
    const rightIcon = computed(() => {
      if (props.type === 'search') {
        return 'close'
      } else if (props.type === 'password' && props.passwordReveal) {
        return isPasswordVisible.value ? 'eye-disabled' : 'eye'
      }
      return ''
    })

    /**
     * @return {Object}
     * @description Returns the input wrap classes for our input
     */
    const wrapClasses = computed(() => {
      return {
        'ak-input--textarea': props.type === 'textarea',
        'ak-input--has-right-icon': false,
        'ak-input--has-left-icon': hasLeftIcon.value,
        flex__item: props.grow,
        'ak-input--inline-edit': props.inlineEdit,
      }
    })

    /**
     * @returns {String}
     * @description Returns the "expandable" class for textareas, this allows
     * them to expand when focused
     */
    const expandableClass = computed(() => {
      return props.expandable ? 'ak-textarea--expanding' : ''
    })

    /**
     * @returns {String}
     * @description Returns the "disabled" class for the input/textarea
     */
    const disabledClass = computed(() => {
      if (!attrs.disabled && !attrs.readonly) return ''
      return 'is-disabled'
    })

    /**
     * @returns {Number}
     * @description Returns the length of the input value this is mainly
     * used by the maxLength counter
     */
    const valueLength = computed(() => {
      if (typeof computedValue.value === 'string') {
        return computedValue.value.length
      } else if (typeof computedValue.value === 'number') {
        return computedValue.value.toString().length
      }
      return 0
    })

    /**
     * @param {Event} event
     * @description Method to set the "computedValue" property when
     * the value changes, this can also be called progromatically
     */
    const onInput = (event) => {
      nextTick(() => {
        /*istanbul ignore else */
        if (event.target) {
          computedValue.value = event.target.value
          validation.isPristine.value = false
        }
      })
    }

    /**
     * @description Toggles the password visibiity status of the input this
     * will just swap it's type from "password" to "text" and back again, it
     * will also focus the input
     */
    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value
      newType.value = isPasswordVisible.value ? 'text' : 'password'
      nextTick(() => {
        input.value.focus()
      })
    }

    /**
     * @description Triggers the required action when the right icon
     * has been clicked
     */
    const rightIconClick = (event) => {
      /*istanbul ignore else */
      if (props.type === 'password' && props.passwordReveal) {
        togglePasswordVisibility()
      } else if (props.type === 'search') {
        computedValue.value = ''
        input.value.focus()
      }
      emit('rightIconClick', event)
    }

    /**
     * @param {Event} event
     * @description Emits the icon click event and focueses the
     * input element
     */
    const iconClick = (event) => {
      nextTick(() => {
        input.value.focus()
        emit('iconClick', event)
      })
    }

    onMounted(() => validation.validate())
    onBeforeUnmount(() => {
      validation.beforeUnmount()
    })

    return {
      input,
      newType,
      computedValue,
      wrapClasses,
      expandableClass,
      hasLeftIcon,
      hasIconRight,
      rightIcon,
      valueLength,
      onInput,
      iconClick,
      togglePasswordVisibility,
      rightIconClick,
      disabledClass,
      isPasswordVisible,
      ...validation,
      ...formMixin,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-input {
  $self: &;
  position: relative;

  &__control {
    &,
    &[type='search'] {
      box-sizing: border-box;
    }
    background-color: #fff;
    border: 1px solid $border-input-color !important;
    border-radius: $base-border-radius;
    box-shadow: $form-box-shadow;
    font-family: $base-font-family;
    font-size: $base-font-size;
    color: $color-text;
    width: 100%;
    padding: calc($spacing / 3) 0 calc($spacing / 3) $spacing-small;
    min-height: $input-total-height;
    border-radius: $base-border-radius;
    transition-property: border-color, box-shadow, background-color, color;
    transition-duration: $transition-time;

    &::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      color: $color-grey-light;
    }
    &::-moz-placeholder {
      /* Firefox 19+ */
      color: $color-grey-light;
    }
    &:-ms-input-placeholder {
      /* IE 10+ */
      color: $color-grey-light;
    }
    &:-moz-placeholder {
      /* Firefox 18- */
      color: $color-grey-light;
    }

    &:hover {
      border-color: darken($border-input-color, 15%);
    }

    &:focus {
      border-color: $color-action;
      box-shadow: $form-box-shadow-focus;
      outline: none;
    }

    &[read-only],
    &[readonly],
    &.is-readonly,
    &[disabled],
    &.is-disabled {
      background-color: $color-grey-lightest;
    }

    &[disabled],
    &.is-disabled {
      color: rgba($color-text, 0.625);
      border-color: lighten($border-input-color, 10%);
      background-color: $color-grey-lightest;
      cursor: not-allowed;
    }

    &--validation-danger {
      border-color: $color-danger !important;
    }
    &--validation-success {
      border-color: $color-success;
    }
    &--validation-warning {
      border-color: $color-warning;
    }

    #{$self}--has-left-icon & {
      padding-left: 2.5em;
    }

    #{$self}--has-right-icon & {
      padding-left: 2.5em;
    }

    #{$self}--textarea & {
      resize: vertical;
    }
  }

  &__icon {
    position: absolute;
    top: 10px;
    font-size: 1.25em;
    display: block;

    &--before {
      left: 0.75em;
    }

    &--after {
      right: 0.75em;
    }

    #{$self}--textarea & {
      top: 1em;
    }
  }

  &__counter {
    position: absolute;
    bottom: 0;
    right: 0;
    margin-bottom: $spacing-small;
    margin-right: $spacing-small;
    background-color: $color-grey-lightest;
    font-size: 0.825em;
    padding: $spacing-tiny;
    line-height: 1;
    border-radius: $base-border-radius;
  }

  &--inline-edit {
    display: flex;
    input {
      align-items: center !important;
      transition: $transition !important;
      border-radius: $base-border-radius !important;
      background-color: inherit !important;
      box-shadow: unset !important;
      padding: $spacing-tiny !important;
      min-height: 0 !important;
      height: auto !important;
      border: 1px solid transparent !important;
      &:not(.is-editing):not(.is-uneditable) {
        &:hover {
          background-color: $color-grey-lightest !important;
          +.ak-input__icon {
            color: $color-primary !important;
          }
        }
        &:focus {
          box-shadow: none !important;
          background-color: lighten($color-action, 30%) !important;
          border: 1px solid lighten($color-action, 20%) !important;
        }
      }
    }

    & .ak-input__icon {
      font-size: unset;
    }
  }
}
</style>
